import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Stack, Typography, useTheme } from '@mui/material';

import Select from 'components/Common/Select';
import Pagination, { usePagination } from 'components/Common/Pagination';
import { BuilderEmptyStateView } from 'components/Common/BuilderEmptyStateView';

import { useGetBusinesses } from '@/apis/agency/hook';
import { useGetAgencyRecommendations } from '@/apis/agency/recommendations/hook';
import { RecommendationStatus } from '@/apis/agency/recommendations/types';
import { BusinessProfileResponse } from '@/apis/business/types';
import CallNeedingAuditForm from '@/pages/Agency/SystemSentiment/components/CallNeedingAuditForm';
import LoadingSkelton from './components/LoadingSkelton';
import RecommendationCard from './components/RecommendationCard';

interface FilterFormData {
  business_id: string;
  status: RecommendationStatus | ' ';
}

const defaultFormValues: FilterFormData = {
  business_id: ' ',
  status: 'pending'
};

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' }
];

const DEFAULT_PER_PAGE = 10;

export default function AgencyRecommendations() {
  const theme = useTheme();
  const { control, watch } = useForm<FilterFormData>({
    defaultValues: defaultFormValues
  });

  const { currentPage, perPage, setPage, setPerPage, resetPage } = usePagination(DEFAULT_PER_PAGE);
  const [appliedFilters, setAppliedFilters] = useState<FilterFormData>(defaultFormValues);
  const [callLogDrawerOpen, setCallLogDrawerOpen] = useState(false);
  const [callLogId, setCallLogId] = useState<string | undefined>();

  const { data: businesses, isLoading: isBusinessesLoading } = useGetBusinesses({
    filter: { account_status_eq: 0 },
    per_page: 9999
  });

  const businessesOptions = useMemo(
    () =>
      ((businesses?.data ?? []) as BusinessProfileResponse[]).map((business) => ({
        value: business.id,
        label: business.name
      })),
    [businesses?.data]
  );

  const watchedValues = watch();
  const watchedFiltersStr = JSON.stringify(watchedValues);

  useEffect(() => {
    setAppliedFilters((prev) => {
      const changed = JSON.stringify(prev) !== watchedFiltersStr;
      if (changed) resetPage();
      return JSON.parse(watchedFiltersStr) as FilterFormData;
    });
  }, [watchedFiltersStr, resetPage]);

  const buildParams = () => {
    const params: Record<string, unknown> = {
      page: currentPage,
      per_page: perPage,
      status: appliedFilters.status && appliedFilters.status !== ' ' ? appliedFilters.status : 'pending'
    };
    const businessId = String(appliedFilters.business_id ?? '').trim();
    if (businessId) params.business_id = businessId;
    return params;
  };

  const { data: recommendations, isLoading } = useGetAgencyRecommendations(buildParams());

  const totalCount = recommendations?.total ?? 0;
  const totalPages = recommendations?.total_pages ?? (perPage > 0 ? Math.max(1, Math.ceil(totalCount / perPage)) : 1);

  return (
    <>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems={{ xs: 'flex-start', lg: 'center' }}
        marginBottom={3}
        flexDirection={{ xs: 'column', lg: 'row' }}
        gap={{ xs: 2, lg: 0 }}
        sx={{ width: '100%' }}
      >
        <Box>
          <Typography variant='h6' fontWeight={500} sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
            Recommendations
          </Typography>
          <Typography color='secondary' fontSize={14}>
            AI recommendations across all businesses
          </Typography>
        </Box>

        <Box
          display='flex'
          gap={1}
          flexWrap='wrap'
          flexDirection={{ xs: 'column', lg: 'row' }}
          sx={{ width: { xs: '100%', lg: 'auto' } }}
        >
          <Box sx={{ width: { xs: '100%', lg: '220px' } }}>
            <Select
              name='business_id'
              label='Business'
              control={control}
              sortOrder='none'
              searchable
              disabled={isBusinessesLoading}
              options={[{ value: ' ', label: 'All businesses' }, ...businessesOptions]}
              sx={{
                height: '40px',
                width: { xs: '100%', lg: '220px' },
                background: theme.palette.background.paper
              }}
            />
          </Box>
          <Box sx={{ width: { xs: '100%', lg: '160px' } }}>
            <Select
              name='status'
              label='Status'
              control={control}
              sortOrder='none'
              options={STATUS_OPTIONS}
              sx={{
                height: '40px',
                width: { xs: '100%', lg: '160px' },
                background: theme.palette.background.paper
              }}
            />
          </Box>
        </Box>
      </Box>

      {isLoading ? (
        <LoadingSkelton />
      ) : recommendations?.data?.length ? (
        <Stack spacing={2}>
          {recommendations.data.map((item) => (
            <RecommendationCard
              key={item.id}
              item={item}
              onOpenCallLog={(id) => {
                setCallLogId(String(id));
                setCallLogDrawerOpen(true);
              }}
            />
          ))}
        </Stack>
      ) : (
        <Box border='1px solid #ECECEC' padding='24px' bgcolor='white' borderRadius='12px'>
          <BuilderEmptyStateView iconWidth={250} iconHeight={130} message='No recommendations found' />
        </Box>
      )}

      <Box sx={{ pb: { xs: 2, lg: 0 } }}>
        <Pagination
          totalPages={totalPages}
          totalCount={totalCount}
          currentPage={currentPage}
          perPage={perPage}
          onChange={setPage}
          onPerPageChange={setPerPage}
        />
      </Box>

      <CallNeedingAuditForm
        view='call-logs'
        logId={callLogId}
        open={callLogDrawerOpen}
        onClose={() => setCallLogDrawerOpen(false)}
      />
    </>
  );
}
